import {
 useContext, useState
} from 'react';
import {
 Card, CardContent, CardFooter, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
 Button
} from 'shared/components/ui/button';
import {
 Badge
} from 'shared/components/ui/badge';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from 'shared/components/ui/dropdown-menu';
import {
 MoreVertical, Pencil, Trash2, Play
} from 'lucide-react';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from 'shared/components/ui/alert-dialog';
import {
 Context
} from 'shared/context/GlobalState';
import {
 jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
 RedisJSONOpcode
} from 'builders/builderOpcode/jsonMode/types';
import {
 useParams, useNavigate
} from 'react-router-dom';

const V2OpCodeBuilderCard = ({
    opCode,
    onManage,
    onDelete,
}: {
    opCode: RedisJSONOpcode;
    onManage: () => void;
    onDelete: (id: string) => void;
}) => {
    const {
 addNewNotifcation
} = useContext(Context);
    const {
 oragID, subOragID
} = useParams();
    const navigate = useNavigate();
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const opcodeId = opCode?.opcode_id || opCode?._id || '';
    const createdDate = opCode?.created
        ? new Date(opCode.created).toLocaleDateString()
        : '';
    const stepCount = opCode?.steps?.length || 0;
    const stepKinds = Array.from(new Set((opCode?.steps || []).map(s => s.kind)));

    const handleDelete = async () => {
        if (!opcodeId) return;
        try {
            setIsDeleting(true);
            await jsonOpcodeService.deleteJSONOpcodes({
                organizationId: oragID,
                subOrganizationId: subOragID,
                opcodeIds: [opcodeId],
            });
            addNewNotifcation('Opcode deleted successfully.', 'success');
            onDelete(opcodeId);
        } catch (error) {
            console.error('Error deleting opcode:', error);
            addNewNotifcation('Failed to delete opcode.', 'danger');
        } finally {
            setIsDeleting(false);
            setShowDeleteAlert(false);
        }
    };

    const handleExecute = () => {
        if (!oragID || !subOragID || !opcodeId) return;
        navigate(`/organizations/${oragID}/sub/${subOragID}/op-codes/json/${opcodeId}/execute`);
    };

    const handleJsonEdit = () => {
        if (!oragID || !subOragID || !opcodeId) return;
        navigate(`/organizations/${oragID}/sub/${subOragID}/op-codes/json/${opcodeId}`);
    };

    const displayTitle = (opCode as any).title || opCode.name || 'Unnamed Opcode';

    return (
        <>
            <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                            <CardTitle className="text-base font-semibold truncate">
                                {displayTitle}
                            </CardTitle>
                            {opCode.description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                    {opCode.description}
                                </p>
                            )}
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="shrink-0">
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={onManage}>
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit (Graphical)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleJsonEdit}>
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit (JSON)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleExecute}>
                                    <Play className="h-4 w-4 mr-2" />
                                    Execute
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => setShowDeleteAlert(true)}
                                    className="text-destructive focus:text-destructive"
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </CardHeader>

                <CardContent className="pb-2">
                    <div className="flex flex-wrap gap-1">
                        {stepKinds.map((kind) => (
                            <Badge key={kind} variant="outline" className="text-xs">
                                {kind}
                            </Badge>
                        ))}
                    </div>
                </CardContent>

                <CardFooter className="text-xs text-muted-foreground justify-between">
                    <span>{stepCount} step{stepCount !== 1 ? 's' : ''}</span>
                    {createdDate && <span>{createdDate}</span>}
                </CardFooter>
            </Card>

            <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Opcode</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{displayTitle}"? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default V2OpCodeBuilderCard;
